import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

const _origDraw = Chart.prototype.draw;
Chart.prototype.draw = function () {
  if (!this.ctx) return;
  return _origDraw.apply(this, arguments);
};

function theme() {
  const dark = document.documentElement.classList.contains('dark');
  return {
    text:       dark ? '#9ca3af' : '#6b7280',
    grid:       dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    gepland:    dark ? '#4b5563' : '#d1d5db',
    uitgevoerd: dark ? '#34d399' : '#059669',
    ma:         dark ? '#f59e0b' : '#d97706',
    rawWeight:  dark ? 'rgba(167,139,250,0.35)' : 'rgba(124,58,237,0.20)',
    tooltipBg:  dark ? '#1f2937' : '#111827',
  };
}

function weekLabel(datum) {
  const d = new Date(datum + 'T00:00:00');
  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
}

// Weekbelasting: gepland vs uitgevoerd (groepeerde staafgrafiek)
export function trainingWeekChart(weken) {
  return {
    _chart:    null,
    _observer: null,
    weken,

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes:      true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    render() {
      this._chart?.destroy();
      this._chart = null;

      const canvas = this.$refs.chart;
      if (!canvas) return;
      const t = theme();

      const labels  = this.weken.map((w) => weekLabel(w.start_datum));
      const gepland  = this.weken.map((w) => Math.round(w.geplande_belasting_min / 60 * 10) / 10);
      const uitgevoerd = this.weken.map((w) => Math.round(w.uitgevoerd_min / 60 * 10) / 10);

      this._chart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels,
          datasets: [
            {
              label:           'Gepland (u)',
              data:            gepland,
              backgroundColor: t.gepland,
              borderRadius:    3,
              order:           2,
            },
            {
              label:           'Uitgevoerd (u)',
              data:            uitgevoerd,
              backgroundColor: t.uitgevoerd,
              borderRadius:    3,
              order:           1,
            },
          ],
        },
        options: {
          responsive:          true,
          maintainAspectRatio: false,
          interaction:         { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              labels:  { color: t.text, font: { size: 11 } },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              padding:         10,
              cornerRadius:    6,
              callbacks: {
                label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} u`,
              },
            },
          },
          scales: {
            x: {
              ticks: { color: t.text, maxTicksLimit: 8 },
              grid:  { display: false },
            },
            y: {
              ticks: {
                color:    t.text,
                callback: (v) => v.toFixed(0) + ' u',
              },
              grid:    { color: t.grid },
              beginAtZero: true,
            },
          },
        },
      });
    },
  };
}

// Koolhydraattrend: g/uur per kwalificerende sessie + wekelijks gemiddelde
export function trainingKoolhydraatChart(entries, weken) {
  return {
    _chart:    null,
    _observer: null,
    entries,
    weken,

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes:      true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    render() {
      this._chart?.destroy();
      this._chart = null;

      const canvas = this.$refs.chart;
      if (!canvas) return;
      const t = theme();

      const sessieLabels = this.entries.map((e) => e.datum);
      const sessieData   = this.entries.map((e) => e.koolhydraten_g_per_uur !== null ? parseInt(e.koolhydraten_g_per_uur, 10) : null);

      const weekLabels = this.weken.map((w) => weekLabel(w.datum));
      const weekData   = this.weken.map((w) => parseInt(w.gemiddelde, 10));

      this._chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels: sessieLabels,
          datasets: [
            {
              label:                'g/uur (sessie)',
              data:                 sessieData,
              borderColor:          t.rawWeight,
              backgroundColor:      'transparent',
              pointBackgroundColor: t.rawWeight,
              pointRadius:          3,
              pointHoverRadius:     5,
              borderWidth:          1,
              spanGaps:             false,
              order:                2,
            },
            {
              label:                'Weekgemiddelde',
              data:                 weekData,
              borderColor:          t.ma,
              backgroundColor:      'transparent',
              pointRadius:          0,
              pointHoverRadius:     4,
              borderWidth:          2,
              spanGaps:             true,
              order:                1,
              xAxisID:              'x',
            },
          ],
        },
        options: {
          responsive:          true,
          maintainAspectRatio: false,
          interaction:         { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              labels:  { color: t.text, font: { size: 11 } },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              padding:         10,
              cornerRadius:    6,
              callbacks: {
                label: (ctx) =>
                  ctx.parsed.y !== null
                    ? `${ctx.dataset.label}: ${ctx.parsed.y} g/uur`
                    : null,
              },
            },
          },
          scales: {
            x: {
              ticks: {
                color:         t.text,
                maxTicksLimit: 10,
                callback(val) {
                  const d = new Date(this.getLabelForValue(val) + 'T00:00:00');
                  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
                },
              },
              grid: { display: false },
            },
            y: {
              ticks: {
                color:    t.text,
                callback: (v) => v + ' g/u',
              },
              grid:        { color: t.grid },
              beginAtZero: true,
            },
          },
        },
      });
    },
  };
}

// Gewichtstrend: ruwe meting + 7-daags voortschrijdend gemiddelde
export function trainingGewichtChart(entries) {
  return {
    _chart:    null,
    _observer: null,
    entries,

    init() {
      this.$nextTick(() => this.render());
      this._observer = new MutationObserver(() => this.render());
      this._observer.observe(document.documentElement, {
        attributes:      true,
        attributeFilter: ['class'],
      });
    },

    destroy() {
      this._observer?.disconnect();
      this._chart?.destroy();
    },

    movingAverage(data, window) {
      return data.map((_, i) => {
        const slice = data.slice(Math.max(0, i - window + 1), i + 1).filter((v) => v !== null);
        return slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null;
      });
    },

    render() {
      this._chart?.destroy();
      this._chart = null;

      const canvas = this.$refs.chart;
      if (!canvas) return;
      const t = theme();

      const labels = this.entries.map((e) => e.datum);
      const rawKg  = this.entries.map((e) => e.gewicht_gram !== null ? e.gewicht_gram / 1000 : null);
      const maKg   = this.movingAverage(rawKg, 7);

      this._chart = new Chart(canvas, {
        type: 'line',
        data: {
          labels,
          datasets: [
            {
              label:                'Gewicht (kg)',
              data:                 rawKg,
              borderColor:          t.rawWeight,
              backgroundColor:      'transparent',
              pointBackgroundColor: t.rawWeight,
              pointRadius:          rawKg.length > 60 ? 0 : 2,
              pointHoverRadius:     4,
              borderWidth:          1,
              spanGaps:             true,
              order:                2,
            },
            {
              label:                '7-daags gem. (kg)',
              data:                 maKg,
              borderColor:          t.ma,
              backgroundColor:      'transparent',
              pointRadius:          0,
              pointHoverRadius:     4,
              borderWidth:          2,
              spanGaps:             true,
              order:                1,
            },
          ],
        },
        options: {
          responsive:          true,
          maintainAspectRatio: false,
          interaction:         { mode: 'index', intersect: false },
          plugins: {
            legend: {
              display: true,
              labels:  { color: t.text, font: { size: 11 } },
            },
            tooltip: {
              backgroundColor: t.tooltipBg,
              padding:         10,
              cornerRadius:    6,
              callbacks: {
                label: (ctx) =>
                  ctx.parsed.y !== null
                    ? `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} kg`
                    : null,
              },
            },
          },
          scales: {
            x: {
              ticks: {
                color:         t.text,
                maxTicksLimit: 10,
                callback(val) {
                  const d = new Date(this.getLabelForValue(val) + 'T00:00:00');
                  return d.toLocaleDateString('nl-BE', { day: 'numeric', month: 'short' });
                },
              },
              grid: { display: false },
            },
            y: {
              ticks: {
                color:    t.text,
                callback: (v) => v.toFixed(1) + ' kg',
              },
              grid: { color: t.grid },
            },
          },
        },
      });
    },
  };
}
